import Swiper from 'swiper';
import { A11y, EffectFade, Navigation } from 'swiper/modules';

export default class FeaturePage {
  /**
   * @param {object} [options]
   * @param {object | null | undefined} [options.lenisController]
   */
  constructor(options = {}) {
    this.lenisController = options.lenisController ?? null;
    this.savedScrollY = 0;
    this.lastOpenTrigger = null;
    this.modalRoot = null;
    this.swiper = null;
    this.sliderRoot = null;
    this.onDocKeydown = this.onDocKeydown.bind(this);
    this.featureDetailModalFunc();
  }

  featureDetailModalFunc() {
    this.modalRoot = document.querySelector('.js-featureDetailModalRoot');
    if (!this.modalRoot) return;

    this.sliderRoot = this.modalRoot.querySelector('.js-featureDetailSlider');
    this.initFeatureDetailSwiper();
    this.bindFeatureDetailOpen();
    this.bindFeatureDetailClose();
    document.addEventListener('keydown', this.onDocKeydown);
  }

  initFeatureDetailSwiper() {
    if (!this.sliderRoot) return;

    const slideCount = this.sliderRoot.querySelectorAll('.swiper-slide').length;
    const minSlides = 2;
    if (slideCount < minSlides) {
      this.sliderRoot.classList.add('featureDetailModal__slider--disableMode');
      return;
    }

    const swiperEl = this.sliderRoot.querySelector('.swiper');
    const nextEl = this.modalRoot.querySelector('.js-featureDetailSliderNext');
    const prevEl = this.modalRoot.querySelector('.js-featureDetailSliderPrev');
    if (!swiperEl || !nextEl || !prevEl) return;

    this.swiper = new Swiper(swiperEl, {
      modules: [A11y, EffectFade, Navigation],
      loop: true,
      slidesPerView: 1,
      centeredSlides: false,
      spaceBetween: 0,
      speed: 600,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      simulateTouch: true,
      allowTouchMove: false,
      grabCursor: false,
      navigation: {
        nextEl,
        prevEl
      },
      a11y: {
        enabled: true,
        prevSlideMessage: '前のスライドへ',
        nextSlideMessage: '次のスライドへ'
      }
    });
  }

  bindFeatureDetailOpen() {
    document.body.addEventListener('click', e => {
      const btn = e.target.closest('.js-featureDetailTrigger');
      if (!btn) return;

      const modalId = btn.getAttribute('data-feature-detail-modal');
      if (!modalId || !this.modalRoot || modalId !== this.modalRoot.id) return;

      const rawIdx = btn.getAttribute('data-feature-detail-index');
      let idx = 0;
      if (rawIdx != null && rawIdx !== '') {
        const parsed = Number.parseInt(rawIdx, 10);
        if (!Number.isNaN(parsed)) idx = parsed;
      }
      this.openFeatureDetailModal(btn);
      requestAnimationFrame(() => {
        if (this.swiper && !this.swiper.destroyed) {
          this.swiper.slideToLoop(idx, 0);
        }
      });
    });
  }

  bindFeatureDetailClose() {
    this.modalRoot
      .querySelectorAll('[data-feature-detail-modal-close]')
      .forEach(btn => {
        btn.addEventListener('click', () => this.closeFeatureDetailModal());
      });

    this.modalRoot.addEventListener('click', e => {
      if (e.target === this.modalRoot) {
        this.closeFeatureDetailModal();
      }
    });
  }

  onDocKeydown(e) {
    if (e.key !== 'Escape') return;
    if (
      !this.modalRoot ||
      this.modalRoot.getAttribute('aria-hidden') !== 'false'
    )
      return;
    this.closeFeatureDetailModal();
  }

  normalizeDocumentElementClasses() {
    document.documentElement.className = document.documentElement.className
      .replace(/\s+/g, ' ')
      .trim();
  }

  lockScroll() {
    const html = document.documentElement;
    const scrollbarWidth = window.innerWidth - html.clientWidth;

    this.lenisController?.stop?.();
    this.normalizeDocumentElementClasses();

    const lenis = this.lenisController?.getInstance?.();
    this.savedScrollY =
      typeof lenis?.scroll === 'number' ? lenis.scroll : window.scrollY;

    html.classList.add('is-modal-open');
    document.body.classList.add('is-modal-open');

    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    if (scrollbarWidth > 0) {
      html.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  unlockScroll() {
    const html = document.documentElement;

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';

    html.classList.remove('is-modal-open');
    document.body.classList.remove('is-modal-open');
    html.style.paddingRight = '';

    window.scrollTo(0, this.savedScrollY);

    this.lenisController?.start?.();
    this.normalizeDocumentElementClasses();
  }

  openFeatureDetailModal(triggerButton) {
    this.lastOpenTrigger = triggerButton ?? null;

    this.modalRoot.setAttribute('aria-hidden', 'false');
    this.lockScroll();

    const focusable = this.modalRoot.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable) {
      setTimeout(() => focusable.focus(), 100);
    }
  }

  closeFeatureDetailModal() {
    this.modalRoot.setAttribute('aria-hidden', 'true');
    this.unlockScroll();

    const triggerButton =
      this.lastOpenTrigger && document.contains(this.lastOpenTrigger)
        ? this.lastOpenTrigger
        : document.querySelector(
            `[data-feature-detail-modal="${this.modalRoot.id}"]`
          );
    this.lastOpenTrigger = null;
    if (triggerButton) {
      triggerButton.focus();
    }
  }
}
